import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { api } from '@/lib/api';
import { Crown, Sparkle, Diamond, ArrowRight, X } from '@phosphor-icons/react';

import { bubbleImageSrc } from '@/lib/bubbleImage';
const TABS = [
  { key: 'trinket', label: 'Trinkets', icon: Sparkle, sub: 'Bubble accessories' },
  { key: 'gem', label: 'Status Gems', icon: Diamond, sub: 'Profile pins' },
];

const RARITY_GRAD = {
  common: 'from-zinc-500 to-zinc-700',
  uncommon: 'from-bx-volt to-bx-cyan',
  rare: 'from-bx-cyan to-blue-600',
  epic: 'from-bx-magenta to-purple-600',
  legendary: 'from-bx-gold to-bx-legendary',
  mythic: 'from-emerald-400 via-bx-magenta to-bx-gold',
};

const TrinketCard = ({ t, onBuy }) => {
  const remaining = t.supply - (t.sold || 0);
  const sold = remaining <= 0;
  return (
    <motion.div
      whileHover={{ y: -3, transition: { duration: 0.2 } }}
      className="glass rounded-2xl p-5 flex flex-col"
      data-testid={`trinket-card-${t.id}`}
    >
      <div className="flex items-center justify-between">
        <span className={`chip rar-${t.rarity}`}>{t.rarity}</span>
        <span className="text-[10px] uppercase tracking-widest text-white/60">
          {remaining}/{t.supply} left
        </span>
      </div>
      <div className={`my-5 mx-auto h-32 w-32 rounded-full bg-gradient-to-br ${RARITY_GRAD[t.rarity]} grid place-items-center text-5xl breathe`}
           style={{ boxShadow: `0 0 50px rgba(255,255,255,0.18)` }}>
        <span>{t.emoji}</span>
      </div>
      <h3 className="font-display text-lg font-black tracking-tighter text-center">{t.name}</h3>
      <p className="text-white/60 text-xs text-center mt-1 flex-1">{t.blurb}</p>
      <div className="mt-4 flex items-center justify-between">
        <div className="font-display font-black text-2xl shimmer-text">${t.price_usd}</div>
        <button
          data-testid={`trinket-buy-${t.id}`}
          disabled={sold}
          onClick={() => onBuy(t)}
          className="btn-primary px-4 py-2 rounded-full text-xs flex items-center gap-1 disabled:opacity-30"
        >
          {sold ? 'Sold' : <>Mint <ArrowRight size={12} weight="bold" /></>}
        </button>
      </div>
    </motion.div>
  );
};

const BubblePicker = ({ open, onClose, onPick }) => {
  const [bubbles, setBubbles] = useState([]);
  useEffect(() => { if (open) api.get('/bubbles/mine').then(({ data }) => setBubbles(data.bubbles)); }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center px-4" onClick={onClose}>
      <div className="glass max-w-xl w-full rounded-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold">Pick a bubble to equip</h3>
          <button data-testid="picker-close" onClick={onClose} className="text-white/65 hover:text-white"><X size={18} weight="bold" /></button>
        </div>
        {bubbles.length === 0 && <div className="text-white/60 text-sm">You need a bubble first. Blow one then come back.</div>}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-80 overflow-y-auto">
          {bubbles.map((b) => (
            <button
              key={b.id}
              data-testid={`picker-pick-${b.id}`}
              onClick={() => onPick(b)}
              className="glass-hi rounded-xl p-3 flex flex-col items-center hover:bg-white/10 transition"
            >
              {bubbleImageSrc(b) ? (
                <img src={bubbleImageSrc(b)} alt={b.name} className="h-16 w-16 rounded-full object-cover" />
              ) : (
                <div className="h-16 w-16 rounded-full bg-gradient-to-br from-bx-cyan to-bx-magenta" />
              )}
              <div className="font-bold text-sm mt-2">{b.name}</div>
              <div className={`text-[10px] uppercase tracking-widest rar-${b.rarity}`}>{b.rarity}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const Trinkets = () => {
  const nav = useNavigate();
  const [tab, setTab] = useState('trinket');
  const [items, setItems] = useState([]);
  const [pendingTrinket, setPendingTrinket] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/trinkets?kind=${tab}`);
    setItems(data.trinkets);
  };
  useEffect(() => { load(); }, [tab]); // eslint-disable-line

  const buy = async (t, bubbleId = null) => {
    try {
      const { data } = await api.post('/trinkets/checkout', {
        trinket_id: t.id,
        bubble_id: bubbleId,
        origin_url: window.location.origin,
      });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'checkout failed');
    }
  };

  const onBuyClick = (t) => {
    if (t.kind === 'trinket') {
      setPendingTrinket(t);
    } else {
      buy(t);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 pb-24">
      <div className="text-center mb-8">
        <Crown size={36} weight="duotone" className="text-bx-gold mx-auto" />
        <h1 className="font-display text-4xl sm:text-6xl font-black tracking-tighter mt-2">
          Trinkets & <span className="shimmer-text">Status Gems</span>
        </h1>
        <p className="text-white/65 mt-2 max-w-2xl mx-auto">
          Equip your bubbles with rare accessories. Pin status gems to your profile. Every piece is supply-capped, ledger-recorded, and one mint per Founder.
        </p>
      </div>

      <div className="flex justify-center gap-2 mb-8">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`trinket-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-5 py-3 rounded-full text-sm font-bold flex flex-col items-start gap-0.5 transition ${
              tab === t.key ? 'bg-bx-cyan text-black' : 'glass-hi text-white/70 hover:bg-white/10'
            }`}
          >
            <span className="flex items-center gap-1.5"><t.icon size={14} weight="bold" /> {t.label}</span>
            <span className="text-[10px] font-normal opacity-70">{t.sub}</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
        {items.map((t) => <TrinketCard key={t.id} t={t} onBuy={onBuyClick} />)}
      </div>

      <BubblePicker
        open={!!pendingTrinket}
        onClose={() => setPendingTrinket(null)}
        onPick={(b) => { const t = pendingTrinket; setPendingTrinket(null); buy(t, b.id); }}
      />
    </div>
  );
};

export default Trinkets;
